import { useState, useCallback, useEffect, useRef } from 'react';
import {
  NexusMessage,
  NexusEntryPoint,
  NexusIntegrationFlow,
  NexusConversationState,
  SendMessagePayload,
} from '../components/steps/IntegrationStep/Nexus/types';
import {
  startConversation as apiStartConversation,
  sendMessage as apiSendMessage,
  fetchConversationHistory as apiFetchConversationHistory,
  reportResumedSession as apiReportResumedSession,
  retryWithBackoff,
  NexusApiError,
  NexusErrorType,
  StartConversationApiPayload,
} from '../components/steps/IntegrationStep/Nexus/nexusClient';
import type { CfNexusStartContext } from '../../cloudflare-integration';
import { REGULAR_NEXUS_THREAD, useNexusThreadId } from './useLocalStorage';
import { OncePerPageLoad, resumedThreadReports as pageResumedThreadReports } from './resumedThreadReports';

/**
 * Constants for Nexus conversation
 */
export const MESSAGE_ID_PREFIX = 'msg_';
export const DEFAULT_WELCOME_MESSAGE =
  "Hello! 👋 I'm Nexus, your **AI-powered** [Prerender.io](https://prerender.io) integration assistant. The information you provide here is processed by an AI system solely to assist with your setup. Please avoid sharing personal or sensitive data.\n\n" +
  "I'll guide you through your setup and help troubleshoot any issues along the way. If something doesn't look right, you can upload screenshots so I can better understand what's happening.\n\n" +
  "Need to brief your developer or support team? I can prepare a summary of what we tried and where we're blocked.\n\n" +
  "🚀 **Let's get started!** Which platform are you integrating with today (e.g., Cloudflare, NGINX, Apache, Next.js)?";


export type OpenChatOptions = { newConversation?: boolean; cfContext?: CfNexusStartContext };

export type NexusConversationDependencies = { resumedThreadReports?: OncePerPageLoad };

const sessionPayloadOf = (
  entryPoint?: NexusEntryPoint,
  integrationFlow?: NexusIntegrationFlow,
): StartConversationApiPayload | undefined =>
  entryPoint || integrationFlow
    ? {
        ...(entryPoint ? { nexus_entry_point: entryPoint } : {}),
        ...(integrationFlow ? { integration_flow: integrationFlow } : {}),
      }
    : undefined;

const revokeImageUrlsOf = (messages: NexusMessage[]): void =>
  messages.forEach((message) => {
    if (message.imageUrl) URL.revokeObjectURL(message.imageUrl);
  });

/**
 * Manages Nexus conversation state, messages, and thread persistence
 */
export function useNexusConversation({
  resumedThreadReports = pageResumedThreadReports,
}: NexusConversationDependencies = {}) {
  const [threadId, setThreadId, storageError, threadFlow] = useNexusThreadId();
  const [messages, setMessages] = useState<NexusMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const messagesRef = useRef(messages);
  const currentEntryPointRef = useRef<NexusEntryPoint | undefined>(undefined);
  // Remembered so an automatic restart after an expired thread still reports
  // which flow the customer is in. Losing it would silently move those sessions
  // into the manual verify bucket.
  const currentIntegrationFlowRef = useRef<NexusIntegrationFlow | undefined>(undefined);
  const currentCfContextRef = useRef<CfNexusStartContext | undefined>(undefined);
  const messageCounterRef = useRef(0);
  const isInitializingRef = useRef(false);
  const conversationGenerationRef = useRef(0);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    return () => {
      revokeImageUrlsOf(messagesRef.current);
    };
  }, []);

  // Handle localStorage errors
  useEffect(() => {
    if (storageError) {
      setError(storageError.message);
    }
  }, [storageError]);

  const startConversation = useCallback(async (
    entryPoint?: NexusEntryPoint,
    integrationFlow?: NexusIntegrationFlow,
    cfContext?: CfNexusStartContext,
  ) => {
    const generation = conversationGenerationRef.current;
    try {
      setIsLoading(true);
      setError(null);

      if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
        console.log('[Nexus] Starting new conversation...');
      }

      // Call real API with retry logic
      const response = await retryWithBackoff(() =>
        apiStartConversation(sessionPayloadOf(entryPoint, integrationFlow), cfContext)
      );
      if (generation !== conversationGenerationRef.current) return;

      setThreadId(response.thread_id, integrationFlow ?? REGULAR_NEXUS_THREAD);
      resumedThreadReports.claim(response.thread_id);

      if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
        console.log('[Nexus] Conversation started successfully:', {
          thread_id: response.thread_id,
        });
      }

      const welcomeMsg: NexusMessage = {
        id: `${MESSAGE_ID_PREFIX}${Date.now()}_${messageCounterRef.current++}`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
      };

      setMessages([welcomeMsg]);
    } catch (err) {
      if (generation !== conversationGenerationRef.current) return;
      if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
        console.error('[Nexus] Error starting conversation:', err);
      }

      if (err instanceof NexusApiError) {
        // User-friendly error messages
        if (err.type === NexusErrorType.SERVICE_UNAVAILABLE) {
          setError(err.message);
        } else if (err.type === NexusErrorType.NETWORK_ERROR) {
          setError('Network error. Please check your connection and try again.');
        } else if (err.type === NexusErrorType.UNAUTHORIZED) {
          // Session expiry is handled globally by SessionExpiredModal (the client
          // dispatches SESSION_EXPIRED on a 401); don't show a competing inline
          // "refresh the page" prompt.
          setError(null);
        } else {
          setError('Failed to start conversation. Please try again.');
        }
      } else {
        setError('Failed to start conversation. Please try again.');
      }
    } finally {
      if (generation === conversationGenerationRef.current) setIsLoading(false);
    }
  }, [setThreadId, resumedThreadReports]);

  const sendMessage = useCallback(
    async (payload: SendMessagePayload) => {
      if (!threadId) {
        setError('No active conversation. Please start a new conversation.');
        return;
      }

      if (!payload.text.trim() && !payload.screenshot) {
        return;
      }

      const generation = conversationGenerationRef.current;
      try {
        setIsLoading(true);
        setError(null);

        if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
          console.log('[Nexus] Sending message:', {
            thread_id: threadId,
            has_screenshot: !!payload.screenshot,
            message_length: payload.text.length,
          });
        }

        // Add user message to UI immediately (optimistic update)
        const userMessage: NexusMessage = {
          id: `${MESSAGE_ID_PREFIX}${Date.now()}_${messageCounterRef.current++}`,
          role: 'user',
          content: payload.text,
          timestamp: new Date(),
          imageFile: payload.screenshot,
          imageUrl: payload.screenshot
            ? URL.createObjectURL(payload.screenshot)
            : undefined,
        };

        setMessages(prev => [...prev, userMessage]);

        // Call real API with retry logic
        const response = await retryWithBackoff(() =>
          apiSendMessage({
            thread_id: threadId,
            message: payload.text,
            screenshot: payload.screenshot,
          })
        );
        if (generation !== conversationGenerationRef.current) return;

        const assistantMessage: NexusMessage = {
          id: `${MESSAGE_ID_PREFIX}${Date.now()}_${messageCounterRef.current++}`,
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
        };

        setMessages(prev => [...prev, assistantMessage]);
      } catch (err) {
        if (generation !== conversationGenerationRef.current) return;
        if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
          console.error('[Nexus] Error sending message:', err);
        }

        if (err instanceof NexusApiError) {
          // Handle invalid thread ID by clearing localStorage and auto-restarting
          if (err.type === NexusErrorType.INVALID_THREAD) {
            if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
              console.log('[Nexus] Invalid thread ID detected, clearing and restarting...');
            }
            setThreadId(null);
            setMessages([]);
            setError('Your session expired. Starting a new conversation...');

            // Automatically restart the conversation
            try {
              await startConversation(
                currentEntryPointRef.current,
                currentIntegrationFlowRef.current,
                currentCfContextRef.current,
              );
              setError(null); // Clear error after successful restart
            } catch (restartErr) {
              if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
                console.error('[Nexus] Failed to restart conversation:', restartErr);
              }
              setError('Failed to restart conversation. Please refresh the page.');
            }
          } else if (err.type === NexusErrorType.SERVICE_UNAVAILABLE) {
            setError(err.message);
          } else if (err.type === NexusErrorType.NETWORK_ERROR) {
            setError('Network error. Please check your connection and try again.');
          } else if (err.type === NexusErrorType.SCREENSHOT_TOO_LARGE) {
            setError(err.message);
          } else if (err.type === NexusErrorType.INVALID_SCREENSHOT_TYPE) {
            setError(err.message);
          } else if (err.type === NexusErrorType.UNAUTHORIZED) {
            // Session expiry is handled globally by SessionExpiredModal (the client
            // dispatches SESSION_EXPIRED on a 401); don't show a competing inline
            // "refresh the page" prompt.
            setError(null);
          } else {
            setError('Failed to send message. Please try again.');
          }
        } else {
          setError('Failed to send message. Please try again.');
        }
      } finally {
        if (generation === conversationGenerationRef.current) setIsLoading(false);
      }
    },
    [threadId, setThreadId, startConversation]
  );

  const openChat = useCallback(async (
    entryPoint?: NexusEntryPoint,
    integrationFlow?: NexusIntegrationFlow,
    { newConversation = false, cfContext }: OpenChatOptions = {},
  ) => {
    setIsChatOpen(true);
    const storedThreadBelongsToFlow = threadFlow == null || threadFlow === (integrationFlow ?? REGULAR_NEXUS_THREAD);
    const startsNewConversation = newConversation || (threadId !== null && !storedThreadBelongsToFlow);
    const selectedEntryPoint = entryPoint || currentEntryPointRef.current;
    if (selectedEntryPoint) {
      currentEntryPointRef.current = selectedEntryPoint;
    }
    if (integrationFlow || startsNewConversation) {
      currentIntegrationFlowRef.current = integrationFlow;
      currentCfContextRef.current = cfContext;
    }

    if (startsNewConversation) {
      conversationGenerationRef.current += 1;
    }
    const generation = conversationGenerationRef.current;

    if ((messagesRef.current.length === 0 && !isInitializingRef.current) || startsNewConversation) {
      isInitializingRef.current = true;

      try {
        if (startsNewConversation) {
          revokeImageUrlsOf(messagesRef.current);
          setMessages([]);
          setThreadId(null);
        }
        if (!threadId || startsNewConversation) {
          // No existing thread - start a new conversation
          await startConversation(selectedEntryPoint, integrationFlow, cfContext);
        } else {
          // Existing thread - fetch conversation history
          setIsLoading(true);

          try {
            const history = await retryWithBackoff(() =>
              apiFetchConversationHistory(threadId)
            );
            if (generation !== conversationGenerationRef.current) return;

            const historyMessages: NexusMessage[] = history.messages.map((msg) => {
              const message: NexusMessage = {
                id: `${MESSAGE_ID_PREFIX}${Date.now()}_${messageCounterRef.current++}`,
                role: msg.role,
                content: msg.content,
                timestamp: new Date(),
              };

              // Include image URL if present (data URL from backend)
              if (msg.image_url) {
                message.imageUrl = msg.image_url;
              }

              return message;
            });

            setMessages(historyMessages);

            if (cfContext && resumedThreadReports.claim(threadId)) {
              apiReportResumedSession(threadId, sessionPayloadOf(selectedEntryPoint, integrationFlow), cfContext).catch(
                () => undefined,
              );
            }
          } catch (err) {
            if (generation !== conversationGenerationRef.current) return;
            if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
              console.error('[Nexus] Failed to fetch conversation history:', err);
            }

            // If history fetch fails (e.g., thread expired), clear threadId and start fresh
            if (err instanceof NexusApiError && err.type === NexusErrorType.INVALID_THREAD) {
              if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
                console.log('[Nexus] Thread expired, starting new conversation');
              }
              setThreadId(null);
              await startConversation(selectedEntryPoint, integrationFlow, cfContext);
            } else {
              // For other errors, show error but keep the thread
              setError('Failed to load conversation history. Starting fresh...');
              await startConversation(selectedEntryPoint, integrationFlow, cfContext);
            }
          } finally {
            if (generation === conversationGenerationRef.current) setIsLoading(false);
          }
        }
      } finally {
        if (generation === conversationGenerationRef.current) isInitializingRef.current = false;
      }
    }
  }, [threadId, threadFlow, setThreadId, startConversation, resumedThreadReports]);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    isInitializingRef.current = false;
    revokeImageUrlsOf(messagesRef.current);
    setMessages([]);
    setError(null);
  }, []);

  const clearConversation = useCallback(() => {
    revokeImageUrlsOf(messagesRef.current);
    setThreadId(null);
    setMessages([]);
    setError(null);
  }, [setThreadId]);

  const state: NexusConversationState = {
    threadId,
    messages,
    isLoading,
    error,
    isChatOpen,
  };

  return {
    state,
    sendMessage,
    openChat,
    closeChat,
    clearConversation,
  };
}
